import { mergeEventHandlers } from '@tableau/widgets';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as ReactResizeDetector from 'react-resize-detector';
import * as TypeStyle from 'typestyle';
import { Body } from './Body';
import { BodyCell } from './BodyCell';
import { FocusIndicatorStyleName } from './DataGridColors';
import { DefaultScrollComponent } from './DefaultScrollComponent';
import { GridSelection } from './GridSelection';
import { HeaderColumn } from './HeaderColumn';
import { HeaderRow } from './HeaderRow';
import { PageIndexes, Paging } from './Paging';
import { RowHeaderCell } from './RowHeaderCell';
import { clamp, Dictionary, isCtrl, measureScrollbarWidth } from './Utils';

export class ConfigDefaults {
  public static readonly rowOuterHeight: number = 38;
  public static readonly sectionRows: number = 20;
  public static readonly colsPerChunk: number = 10;
  public static readonly maxInitialColumnWidth: number = 200;
  public static readonly minimumColumnWidth: number = 39;
  public static readonly rememberLastScrollPos: boolean = false;
}

/** Whether column headers are shown, i.e. as the first row of the grid. */
export const ColumnHeadersContext = React.createContext<boolean>(true);
ColumnHeadersContext.displayName = 'DataGridColumnHeadersContext';

type FocusLocation = 'body' | 'header' | 'outside';

export class DataGrid extends React.Component<DataGrid.Props & React.HTMLAttributes<HTMLElement>, DataGrid.State> {
  private static defaultScroller: React.ComponentClass<DataGrid.ScrollComponentProps> = DefaultScrollComponent;

  private mergedMeasuredColumnHeaderWidths: DataGrid.ColumnWidths | undefined = undefined;
  private mergedMeasuredColumnBodyWidths: DataGrid.ColumnWidths | undefined = undefined;

  private body: Body | null = null;
  private config: DataGrid.Config = {};
  private colsPerChunk: number = 0;
  private sectionRows: number = 0;
  private rowOuterHeight: number = 0;
  private minimumColumnWidth: number = 0;
  // TFSID 1082053: We need to compare the previous render's value against the current value to see if column widths need adjusting.
  private scrollbarWasNeeded: boolean = false;
  private scrollTop: number = 0;

  // Focus Management
  /* 'active' here means "where focus was most recently observed" --  i.e. even if the user has
     focused outside the DataGrid, we want to keep track of the previous row/col */
  private activeRowIndex: number = 0;
  private activeColIndex: number = 0;
  /** when true, keyboard navigation will be disabled as the user is e.g. editing an `<input>` inside a cell.  */
  private focusInsideCell: boolean = false;
  /** When true, all scroll operations noop -- used to prevent scrolling contention when paging in new cells for a focus action. */
  private aboutToFocusCell: boolean = false;

  private gridElement: HTMLElement | null = null;
  private bodyElement: HTMLElement | null = null;
  private headerElement: HTMLElement | null = null;

  private readonly defaultHeaderHeight: number = 37;
  private readonly defaultAverageColumnWidth: number = 80;

  public state: DataGrid.State = {
    focusLocation: 'outside',
    focusVisible: true,
    measuredColumnHeaderWidths: undefined,
    measuredColumnBodyWidths: undefined,
    userSizedColumnWidths: {},
    columnWidths: undefined,
    averageColumnWidth: 0,
    scrollLeft: 0,
    rows: [],
    gridWidth: 0,
    gridHeight: 0,
    pageIndexes: {
      startColIdx: 0,
      endColIdx: -1,
      startRowIdx: 0,
      endRowIdx: -1
    },
    selectionTimestamp: 0,
  };
  private setStateIfNew(stateUpdate: Partial<DataGrid.State>, callback?: () => void): void {
    for (let key of Object.keys(stateUpdate) as Array<keyof DataGrid.State>) {
      if (stateUpdate[key] !== this.state[key]) {
        this.setState(stateUpdate as DataGrid.State, callback);
        return;
      }
    }
  }

  public static displayName: string = 'DataGrid';

  public static defaultProps: Pick<DataGrid.Props, 'cols' | 'showHeaders'> = {
    cols: [],
    showHeaders: true,
  };

  public componentDidMount(): void {
    const rect = this.getGridClientRect();
    const gridWidth = Math.floor(rect.width);
    const gridHeight = Math.floor(rect.height);
    const pageIndexes = this.getPageIndexesFromScrollPosition(this.state.scrollLeft, this.props.cols);

    const stateUpdate = {
      gridWidth,
      gridHeight,
      columnWidths: this.updateColumnWidths({}, {}, this.props.cols, gridWidth),
      pageIndexes
    } as DataGrid.State

    this.fetchUsingPageIndexes(pageIndexes, stateUpdate);
  }

  public UNSAFE_componentWillReceiveProps(nextProps: DataGrid.Props): void {
    if (this.props.rowStore !== nextProps.rowStore) {
      // A new row store is created when order or filter params have changed, so
      // scroll to the top (unless user explicitly sets rememberLastScrollPos in the config).
      if (!!this.body) {
        this.body.onRowStoreChanged();
      }
    }

    this.fetchForNextProps(nextProps);
  }

  public componentDidUpdate(): void {
    this.maybeUpdateStateForMeasuredColumnWidths();
  }

  // Sometimes we don't know that we need to update the column widths until after the data-grid has rendered.
  // This method checks if that is the case, and if so, updates the column widths.
  private maybeUpdateStateForMeasuredColumnWidths(): void {
    let isColumnWidthChanged: boolean = false;
    const gridWidth: number = this.getGridWidth();
    const newColumnWidths: DataGrid.ColumnWidths = this.getCalculatedColumnWidths(gridWidth);
    const scrollbarIsNeeded: boolean = this.computeIsScrollbarNeeded();

    Object.keys(newColumnWidths || {}).some(key => {
      if (newColumnWidths![key] !== (this.state.columnWidths && this.state.columnWidths[key])) {
        isColumnWidthChanged = true;
      }
      return isColumnWidthChanged;
    });

    // TFSID 1082053: If a vertical scrollbar is needed, we'll need to recalculate the column widths to account for that space
    if (scrollbarIsNeeded !== this.scrollbarWasNeeded) {
      isColumnWidthChanged = true;
      this.scrollbarWasNeeded = scrollbarIsNeeded;
    }

    if (isColumnWidthChanged) {
      this.updateStateForMeasuredColumnWidths();
    }
  }

  public focusCell(rowIdx: number, colIdx: number = this.activeColIndex) {
    if (this.state.rows.length === 0 || this.props.cols.length === 0) { return; }

    rowIdx = clamp(rowIdx, 0, Math.max(0, this.state.rows.length - 1)); // 0 (not -1) because column headers aren't yet keyboard-accessible
    colIdx = clamp(colIdx, 0, Math.max(0, this.props.cols.length - 1));

    const cellElem = this.findCellElement({ colIdx, rowIdx });
    if (!cellElem) { // attempting to focus a cell which (for perf reasons) is not currently rendered in the DOM
      this.aboutToFocusCell = true; // disable body scroll position fixing in order to page in the cell-to-focus
      this.setState({
        pageIndexes: Paging.getSectionedPageIndexesForCell(
          colIdx,
          rowIdx,
          this.state.rows.length || Math.ceil(this.rowsWithinViewPort()),
          this.props.cols,
          this.state.columnWidths || {},
          this.minimumColumnWidth,
          this.getBodyClientRect(),
          this.rowOuterHeight,
          this.colsPerChunk,
          this.sectionRows
        )
      }, () => {
        this.aboutToFocusCell = false;
        // call this method again now that the cell-to-focus is rendered
        this.focusCell(rowIdx, colIdx);
      });
      return;
    }

    if (this.focusInsideCell) {
      const focusableChild = cellElem.querySelector<HTMLElement>(
        'input:enabled, textarea:enabled, select:enabled, button:enabled, a[href], [tabindex]'
      );
      if (focusableChild) {
        focusableChild.focus();
        return;
      } else {
        this.focusInsideCell = false;
      }
    }

    cellElem.focus();
  }

  private focusActiveCell(): void {
    this.focusCell(this.activeRowIndex, this.activeColIndex);
  }

  /** Returns the column and row index for the currently-active cell */
  public getActiveCell(): DataGrid.Cell {
    return { rowIdx: this.activeRowIndex, colIdx: this.activeColIndex, colKey: this.props.cols[this.activeColIndex].dataKey, rowKey: this.state.rows[this.activeRowIndex].key };
  }

  private findCellElement(cell: DataGrid.Cell): HTMLElement | null {
    if (cell.rowIdx < 0) {
      return this.headerElement?.querySelector(`[role="columnheader"][aria-colindex="${cell.colIdx + 1}"]`) ?? null;
    }
    return this.bodyElement?.querySelector(`[aria-rowindex="${cell.rowIdx + (this.props.showHeaders ? 2 : 1)}"][aria-colindex="${cell.colIdx + 1}"][role="gridcell"]`) ?? null;
  }

  private removeStaleColumnWidths(nextProps: DataGrid.Props): DataGrid.State {
    const trimmedColumnWidths: DataGrid.ColumnWidths = {};
    const trimmedMeasuredColumnHeaderWidths: Dictionary<number> = {};
    const trimmedMeasuredColumnBodyWidths: Dictionary<number> = {};

    nextProps.cols.forEach(col => {
      const columnKey = col.name;
      if (this.state.columnWidths && this.state.columnWidths[columnKey]) {
        trimmedColumnWidths[columnKey] = this.state.columnWidths[columnKey];
      }
      if (this.state.measuredColumnHeaderWidths && this.state.measuredColumnHeaderWidths[columnKey]) {
        trimmedMeasuredColumnHeaderWidths[columnKey] = this.state.measuredColumnHeaderWidths[columnKey];
      }
      if (this.state.measuredColumnBodyWidths && this.state.measuredColumnBodyWidths[columnKey]) {
        trimmedMeasuredColumnBodyWidths[columnKey] = this.state.measuredColumnBodyWidths[columnKey];
      }
    });

    return {
      columnWidths: trimmedColumnWidths,
      measuredColumnHeaderWidths: trimmedMeasuredColumnHeaderWidths,
      measuredColumnBodyWidths: trimmedMeasuredColumnBodyWidths
    } as DataGrid.State;
  }

  private areColumnsEqual = (nextCols: DataGrid.Column[]): boolean => {
    if (this.props.cols.length !== nextCols.length) {
      return false;
    }

    for (let idx = 0; idx < nextCols.length; idx++) {
      if (this.props.cols[idx].name !== nextCols[idx].name) {
        return false;
      }
    }

    return true;
  }

  private arePageIndexesEqual(currentPageIndexes: PageIndexes, nextPageIndexes: PageIndexes): boolean {
    return currentPageIndexes.startColIdx === nextPageIndexes.startColIdx &&
      currentPageIndexes.endColIdx === nextPageIndexes.endColIdx &&
      currentPageIndexes.startRowIdx === nextPageIndexes.startRowIdx &&
      currentPageIndexes.endRowIdx === nextPageIndexes.endRowIdx;
  }

  private isPageIndexesEqualToInitialValue = (): boolean => {
    return this.state.pageIndexes.startColIdx === 0 &&
      this.state.pageIndexes.endColIdx === -1 &&
      this.state.pageIndexes.startRowIdx === 0 &&
      this.state.pageIndexes.endRowIdx === -1;
  }

  private fetchForNextProps = (nextProps: DataGrid.Props): void => {
    const partialStateUpdate = this.removeStaleColumnWidths(nextProps);

    if (!this.areColumnsEqual(nextProps.cols) || this.isPageIndexesEqualToInitialValue() || this.props.rowStore !== nextProps.rowStore) {
      partialStateUpdate.columnWidths = this.updateColumnWidths(
        partialStateUpdate.measuredColumnBodyWidths || {},
        partialStateUpdate.measuredColumnHeaderWidths || {},
        nextProps.cols,
        this.state.gridWidth);

      partialStateUpdate.pageIndexes = this.getPageIndexesFromScrollPosition(this.state.scrollLeft, nextProps.cols);

      this.fetchUsingProps(
        nextProps,
        partialStateUpdate.pageIndexes ? partialStateUpdate.pageIndexes.startRowIdx : this.state.pageIndexes.startRowIdx,
        partialStateUpdate.pageIndexes ? partialStateUpdate.pageIndexes.endRowIdx : this.state.pageIndexes.endRowIdx,
        partialStateUpdate
      );
    }
  }

  private getGridClientRect = (): ClientRect => {
    if (!this.gridElement) {
      return { width: 0, height: 0 } as ClientRect;
    }
    return this.gridElement.getBoundingClientRect();
  }

  private getBodyClientRect = (): ClientRect => {
    if (!this.bodyElement) {
      return { width: 0, height: 0 } as ClientRect;
    }
    return this.bodyElement.getBoundingClientRect();
  }

  private onResize = (): void => {
    if (!this.gridElement) { return; }

    const rect = this.getGridClientRect();
    const gridHeight = Math.floor(rect.height);
    const gridWidth = Math.floor(rect.width);
    const columnWidths = this.updateColumnWidths(this.state.measuredColumnBodyWidths || {}, this.state.measuredColumnHeaderWidths || {}, this.props.cols, gridWidth);
    const pageIndexes = this.getPageIndexesFromScrollPosition(this.state.scrollLeft, this.props.cols);

    const stateUpdate = {
      columnWidths,
      gridHeight,
      gridWidth,
      pageIndexes
    } as DataGrid.State

    if (this.isFetchRequiredForPageIndexes(pageIndexes)) {
      this.fetchUsingPageIndexes(pageIndexes, stateUpdate);
    } else {
      this.setState(stateUpdate);
    }
  }

  private resizeProps: ReactResizeDetector.default['props'] = {
    onResize: this.onResize,
    handleWidth: true,
    handleHeight: true,
    skipOnMount: false,
    refreshMode: 'debounce',
    refreshRate: 0,
  };

  private isFetchRequiredForPageIndexes(nextPageIndexes: PageIndexes): boolean {
    return nextPageIndexes.startRowIdx < this.state.pageIndexes.startRowIdx || nextPageIndexes.endRowIdx > this.state.pageIndexes.endRowIdx;
  }

  private fetchUsingPageIndexes = (pageIndexes: PageIndexes, partialStateUpdate?: DataGrid.State): void => {
    this.fetchUsingProps(this.props, pageIndexes.startRowIdx, pageIndexes.endRowIdx, partialStateUpdate);
  }

  private fetchUsingProps = (props: DataGrid.Props, start: number, end: number, partialStateUpdate?: DataGrid.State): void => {
    start = Math.max(start, 0);
    end = Math.max(end, 0);

    if (props.rowStore) {
      props.rowStore.getItems(start, end, (rows: DataGrid.Row[]) => {
        if (!rows || !this.gridElement) {
          return;
        }

        this.setState({ rows, ...partialStateUpdate } as DataGrid.State, () => {
          if (this.props.updated) {
            this.props.updated(rows);
          }

          const nextPageIndexes = this.getPageIndexesFromScrollPosition(this.state.scrollLeft, props.cols);
          if (!this.arePageIndexesEqual(this.state.pageIndexes, nextPageIndexes)) {
            const stateUpdate = { pageIndexes: nextPageIndexes } as DataGrid.State;
            this.fetchUsingProps(this.props, nextPageIndexes.startRowIdx, nextPageIndexes.endRowIdx, stateUpdate);
          }
        });
      });
    } else if (partialStateUpdate && Object.keys(partialStateUpdate).length > 0) {
      this.setState(partialStateUpdate);
    }
  }

  private onColumnResize = (column: DataGrid.Column, width: number) => {
    let newColumnWidth: Dictionary<number> = {};
    newColumnWidth[column.name] = width;

    this.setState({
      userSizedColumnWidths: { ...this.state.userSizedColumnWidths, ...newColumnWidth },
      columnWidths: { ...this.state.columnWidths, ...newColumnWidth }
    } as DataGrid.State);
  }

  private handleMeasuredColumnHeaderWidths = (widths: Dictionary<number>) => {
    this.mergedMeasuredColumnHeaderWidths = { ...this.state.measuredColumnHeaderWidths || {}, ...widths }
    this.updateStateForMeasuredColumnWidths();
  }

  private handleMeasuredColumnBodyWidths = (widths: Dictionary<number>) => {
    this.mergedMeasuredColumnBodyWidths = { ...this.state.measuredColumnBodyWidths || {}, ...widths }
    this.updateStateForMeasuredColumnWidths();
  }

  private getCalculatedColumnWidths(gridWidth: number): DataGrid.ColumnWidths {
    return this.updateColumnWidths(
      this.mergedMeasuredColumnBodyWidths || {},
      this.mergedMeasuredColumnHeaderWidths || {},
      this.props.cols,
      gridWidth
    );
  }

  private getGridWidth(): number {
    return this.state.gridWidth || Math.floor(this.getGridClientRect().width);
  }

  /**
   * This method uses the measured column widths from the body and header components to determine each column's width.
   * Typically, a column width will be the maximum width of the two measurements.
   */
  private updateStateForMeasuredColumnWidths(): void {
    const haveColumnBodyWidthMeasurements = (this.state.rows.length === 0 || this.mergedMeasuredColumnBodyWidths);
    const haveColumnHeaderWidthMeasurements = (!this.props.showHeaders || this.mergedMeasuredColumnHeaderWidths);

    if (haveColumnBodyWidthMeasurements && haveColumnHeaderWidthMeasurements) {
      const gridWidth: number = this.getGridWidth();

      const newColumnWidths = this.getCalculatedColumnWidths(gridWidth);

      const newAverageColumnWidth = this.calculateAverageColumnWidth(newColumnWidths);

      const columnWidthsStateUpdate = {
        gridWidth,
        columnWidths: newColumnWidths,
        averageColumnWidth: newAverageColumnWidth,
        measuredColumnBodyWidths: { ...this.mergedMeasuredColumnBodyWidths },
        measuredColumnHeaderWidths: { ...this.mergedMeasuredColumnHeaderWidths }
      } as DataGrid.State;

      this.setState(columnWidthsStateUpdate);
    }
  }

  private computeIsScrollbarNeeded(): boolean {
    if (!this.bodyElement || this.state.rows.length === 0) {
      return false;
    }
    const totalRowHeight = this.rowOuterHeight * this.state.rows.length;
    const bodyHeight = this.getBodyClientRect().height;

    return totalRowHeight > bodyHeight;
  }

  private getScrollbarWidth(): number {
    return this.computeIsScrollbarNeeded() ? measureScrollbarWidth(this.bodyElement!) : 0;
  }

  private updateColumnWidths(
    measuredColumnBodyWidths: DataGrid.ColumnWidths,
    measuredColumnHeaderWidths: DataGrid.ColumnWidths,
    cols: DataGrid.Column[],
    gridWidth: number
  ): DataGrid.ColumnWidths {
    const maxInitialColumnWidth = this.config.maxInitialColumnWidth || ConfigDefaults.maxInitialColumnWidth;

    let columnWidths: Dictionary<number> = {};
    let maxMeasuredColumnWidths: Dictionary<number> = {};
    let totalWidth = 0;

    cols.forEach((col: DataGrid.Column) => {
      let currentColumnBodyWidth: number | undefined;
      let currentColumnHeaderWidth: number | undefined;
      let columnKey = col.name;
      let maxColumnWidth: number = 0;

      if (this.state.userSizedColumnWidths[columnKey]) {
        columnWidths[columnKey] = this.state.userSizedColumnWidths[columnKey];
        totalWidth += columnWidths[columnKey];
      } else {
        if (!col.preferredWidth) {
          currentColumnBodyWidth = measuredColumnBodyWidths[columnKey];
          currentColumnHeaderWidth = measuredColumnHeaderWidths[columnKey];
          maxColumnWidth = Math.max(currentColumnBodyWidth || 0, currentColumnHeaderWidth || 0);
        }
        maxMeasuredColumnWidths[columnKey] = col.preferredWidth || maxColumnWidth;

        if (col.preferredWidth || currentColumnBodyWidth || currentColumnHeaderWidth) {
          const currentMaxInitialColumnWidth = (maxInitialColumnWidth < 0) ? maxMeasuredColumnWidths[columnKey] : maxInitialColumnWidth;
          columnWidths[columnKey] = Math.min(maxMeasuredColumnWidths[columnKey], currentMaxInitialColumnWidth);
          totalWidth += columnWidths[columnKey];
        }
      }
    });

    if (this.props.showRowHeaders) {
      totalWidth += RowHeaderCell.Width;
    }

    // Allocating unused width in left-to-right column order, up to each columns's max width
    let extraAvailable = gridWidth - this.getScrollbarWidth() - (this.props.leftMargin || 0) - totalWidth;
    let growToFitCols = 0;

    for (let colIdx = 0; colIdx < cols.length && extraAvailable > 0; colIdx++) {
      const col = cols[colIdx];
      const maxColWidth: number = col.maxColumnWidth || maxMeasuredColumnWidths[col.name];
      const extraRequested: number = Math.min(maxColWidth, maxMeasuredColumnWidths[col.name]) - columnWidths[col.name];

      if (extraRequested > 0 && columnWidths[col.name] !== undefined) {
        const extraAllocated = Math.min(extraRequested, extraAvailable);
        columnWidths[col.name] += extraAllocated;
        extraAvailable -= extraAllocated;
      }

      if (col.growToFit && !this.state.userSizedColumnWidths[col.name]) {
        growToFitCols += 1;
      }
    }

    // Allocate remaining width evenly to any growToFit columns
    if (extraAvailable > 0 && growToFitCols > 0) {
      const extraWidth = extraAvailable / growToFitCols;
      cols.forEach((col: DataGrid.Column) => {
        if (col.growToFit && !this.state.userSizedColumnWidths[col.name] && columnWidths[col.name] !== undefined) {
          columnWidths[col.name] += extraWidth;
        }
      });
    }

    return columnWidths;
  }

  private handleRef = (gridElement: HTMLDivElement | null): void => {
    if (gridElement) {
      // React doesn't have a captured mouseEnter event so we bind the native version here.
      // This could be trivially switched to onMouseOverCapture, but that is a breaking change
      // for any consumers watching for `mouseenter` selection events. TFSID 882922
      gridElement.addEventListener('mouseenter', this.handleMouseSelection as any, true);

      // React doesn't fire synthetic `blur` events when a focused child element is removed from the DOM
      // So we bind a focusout handler here for listening to bubbling blur events
      // See https://github.com/facebook/react/issues/9142
      gridElement.addEventListener('focusout', this.handleBlur)
    }
    this.gridElement = gridElement;
  }

  private handleBodyRef = (body: Body) => {
    this.body = body;
    try {
      this.bodyElement = ReactDOM.findDOMNode(body) as HTMLElement;
    } catch (e) {
      this.bodyElement = null;
    }
    this.updateStateForMeasuredColumnWidths();
  }

  private handleHeaderRef = (header: HeaderRow) => {
    try {
      this.headerElement = ReactDOM.findDOMNode(header) as HTMLElement;
    } catch (e) {
      this.headerElement = null;
    }
  }

  private getFocusLocationFromNode(node: Node): FocusLocation {
    if (this.bodyElement && this.bodyElement.contains(node)) {
      return 'body';
    } else if (this.headerElement && this.headerElement.contains(node)) {
      return 'header';
    } else {
      return 'outside';
    }
  }

  private handleBlur: (e: FocusEvent) => void = e => {
    const relatedTarget = e.relatedTarget as Node;
    const focusLocation = this.getFocusLocationFromNode(relatedTarget);
    if (focusLocation === 'outside') {
      this.focusInsideCell = false;
    }

    // Default `focusVisible` to `true` when focus leaves the grid, so that the indicator shows on an initial TAB-into the grid. When
    // a mouse interaction focuses the grid, the mouseDown event precedes the focus event, so by setting `focusVisible` to `false` in that
    // handler, the indicator will never show (i.e. no flicker) for mouse-focus, and will clear if the user switches from keyboard to mouse
    this.setStateIfNew({ focusLocation, focusVisible: this.state.focusVisible || focusLocation === 'outside' });
  }

  private handleFocus: React.FocusEventHandler<HTMLElement> = mergeEventHandlers(e => {
    if (e.target === e.currentTarget && this.state.focusVisible) { // we either tabbed onto the grid element, or clicked it -- ignore the latter
      this.focusActiveCell(); // redirect focus onto active cell element
      return;
    }

    const targetCell = this.getCellFromEventTarget(e.target);
    if (!targetCell) { return; }

    const targetRole = e.target.getAttribute('role');
    this.focusInsideCell = (targetRole !== 'gridcell' && targetRole !== 'columnheader');

    this.activeColIndex = targetCell.colIdx;
    this.activeRowIndex = targetCell.rowIdx;
    this.setStateIfNew({ focusLocation: targetCell.rowIdx < 0 ? 'header' : 'body' });
  }, e => this.props.onFocus?.(e));

  private handleSelectRowClick = (rowIdx: number, e: React.MouseEvent<HTMLDivElement>): void => {
    this.dispatchSelectionEvent(-1, rowIdx, e);
  };

  private handleSelectAllClick = (e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>): void => {
    this.dispatchSelectionEvent(-1, -1, e);
  };

  private dispatchSelectionEvent(colIdx: number, rowIdx: number, e: MouseEvent | React.MouseEvent | React.KeyboardEvent): void {
    if (!this.props.onSelectionEvent) { return; }

    const colKey = colIdx >= 0 && this.props.cols[colIdx] ? this.props.cols[colIdx].dataKey : undefined;
    const rowKey = rowIdx >= 0 && this.state.rows[rowIdx] ? this.state.rows[rowIdx].key : undefined;

    const { altKey, shiftKey, key, button, buttons, type } = e as MouseEvent & Pick<React.KeyboardEvent, 'key'>
    const ctrlKey = isCtrl(e);

    const selectionEvent: DataGrid.CellSelectionEvent = {
      cell: { colIdx, rowIdx, colKey, rowKey }, altKey, ctrlKey, shiftKey, key, button, buttons, type
    };

    if (this.props.onSelectionEvent(selectionEvent)) {
      this.setState({ selectionTimestamp: this.state.selectionTimestamp + 1 });
    }
  }

  public render(): JSX.Element {
    // tslint:disable-next-line:deprecation max-line-length
    const { cols, config, emptyChild, hackyUpdate, headerHeight: headerHeightProp, leftMargin, onCellClick, onCellContextMenu, onCellDoubleClick, onHeaderClick, onHeaderContextMenu, onHeaderDoubleClick, onSelectionEvent, rowStore, scrollComponent, scrollableHandler, scrollingRootId, selectionModel, showHeaders, showHorizontalGridLines, showRowBanding, showRowHeaders, showVerticalGridLines, sortAscending, sortKey, style, updated, ...htmlProps } = this.props;

    this.config = config || {}; // bad practice right here
    this.colsPerChunk = this.config.colsPerChunk || ConfigDefaults.colsPerChunk;
    this.sectionRows = this.config.sectionRows || ConfigDefaults.sectionRows;
    this.rowOuterHeight = this.config.rowOuterHeight || ConfigDefaults.rowOuterHeight;
    this.minimumColumnWidth = this.config.minimumColumnWidth || ConfigDefaults.minimumColumnWidth;

    const rows = this.state.rows;
    const headerHeight = showHeaders ? (headerHeightProp || this.defaultHeaderHeight) : 0;
    const columnWidths = this.state.columnWidths;
    const scrollLeft = this.state.scrollLeft || 0;
    const columnWidthInfo = this.getColumnWidthInfo();
    const pageIndexes = this.state.pageIndexes;
    const colEndIdx = Math.min(pageIndexes.endColIdx, cols.length - 1);

    let children: React.ReactChild[] = [];

    if (Array.isArray(rows)) {
      if (colEndIdx >= pageIndexes.startColIdx && showHeaders) {
        children.push(<HeaderRow {...{
          key: 'header',
          ref: this.handleHeaderRef,
          headerHeight,
          rowCount: rows.length,
          cols,
          colStartIdx: pageIndexes.startColIdx,
          colEndIdx,
          minColWidth: this.minimumColumnWidth,
          columnWidths: columnWidths || this.getProvisionalColumnWidths(),
          leftMargin: columnWidthInfo.leftSpacer + (leftMargin || 0),
          sortKey,
          sortAscending: sortAscending !== false,
          onHeaderClick,
          showVerticalGridLines,
          onRowHeaderCellClick: showRowHeaders ? this.handleSelectAllClick : undefined,
          onHeaderDoubleClick,
          onHeaderContextMenu,
          measuredColumnWidths: this.state.measuredColumnHeaderWidths || {},
          measureColumnWidths: this.handleMeasuredColumnHeaderWidths,
          scrollLeft,
          onColumnResize: this.onColumnResize,
          selectionModel,
          selectionTimestamp: this.state.selectionTimestamp,
        }} />);
      }

      children.push(<Body {...{
        key: 'body',
        ref: this.handleBodyRef,
        headerHeight,
        showRowBanding,
        showHorizontalGridLines,
        showVerticalGridLines,
        cols,
        columnWidths,
        leftMargin,
        leftSpacer: columnWidthInfo.leftSpacer,
        measuredColumnWidths: this.state.measuredColumnBodyWidths || {},
        measureColumnWidths: this.handleMeasuredColumnBodyWidths,
        onBodyScroll: this.onBodyScroll,
        rows,
        emptyChild,
        selectionModel,
        selectionTimestamp: this.state.selectionTimestamp,
        onCellClick,
        onCellDoubleClick,
        onCellContextMenu,
        onRowHeaderCellClick: showRowHeaders ? this.handleSelectRowClick : undefined,
        scrollComponent: scrollComponent || DataGrid.defaultScroller,
        scrollingRootId,
        onIScrollableRefUpdate: scrollableHandler,
        rowOuterHeight: this.config.rowOuterHeight || ConfigDefaults.rowOuterHeight,
        colsPerChunk: this.config.colsPerChunk || ConfigDefaults.colsPerChunk,
        sectionRows: this.config.sectionRows || ConfigDefaults.sectionRows,
        rememberLastScrollPos: this.config.rememberLastScrollPos || ConfigDefaults.rememberLastScrollPos,
        pageIndexes,
        totalColumnWidth: columnWidthInfo.totalColumnWidth
      }} />);
    }

    const rowCount = this.state.rows.length + (showHeaders ? 1 : 0);

    return (
      <div role="grid"
        aria-colcount={cols.length}
        aria-rowcount={rowCount}
        {...htmlProps}
        className={TypeStyle.classes(htmlProps.className, 'tb-react-data-grid', FocusIndicatorStyleName, { 'focus-visible': this.state.focusVisible })}
        onMouseDown={this.handleMouseSelection}
        onKeyDown={this.handleKeyDown}
        onFocus={this.handleFocus}
        ref={this.handleRef}
        style={{
          // DefaultScrollComponent is absolutely positioned so the grid element needs to be the relative parent
          position: 'relative',
          overflow: 'hidden',
          height: '100%',
          outline: 'none',
          ...style,
        }}
        tabIndex={this.state.focusLocation === 'outside' ? 0 : undefined} // `undefined` when inside the grid prevents a focus trap when shift-tabbing
      >
        <ColumnHeadersContext.Provider value={!!showHeaders}>
          {children}
        </ColumnHeadersContext.Provider>
        <ReactResizeDetector.default {...this.resizeProps} />
      </div>
    );
  }

  private getCellFromEventTarget(target: EventTarget | null): DataGrid.Cell | null {
    let targetCell: HTMLElement | null = target as HTMLElement;

    // IE11 doesn't have Element.closest so we walk the tree instead
    while (targetCell) {
      const role = targetCell.getAttribute('role');
      if (role === 'gridcell' || role === 'columnheader') {
        break;
      } else if (role === 'separator') {
        if (targetCell.parentElement && targetCell.parentElement.getAttribute('role') === 'columnheader') {
          // don't interfere with the mouse drag handler for column resizing
          return null;
        }
      } else if (targetCell === this.gridElement) {
        // don't go outside our tree
        return null;
      }
      targetCell = targetCell.parentElement;
    }
    if (!targetCell) { return null; }

    return {
      // aria-rowindex and aria-colindex count from 1, not 0
      rowIdx: parseInt(targetCell.getAttribute('aria-rowindex') || '', 10) - (this.props.showHeaders ? 2 : 1),
      colIdx: parseInt(targetCell.getAttribute('aria-colindex') || '', 10) - 1
    }
  }

  private handleMouseSelection = (e: MouseEvent | React.MouseEvent): void => {
    if (this.state.focusVisible && e.type.indexOf('down') > 0) {
      this.setState({ focusVisible: false }); // see comments in `handleBlur`
    };
    if (!this.props.onSelectionEvent || (e.type === 'mouseenter' && e.buttons !== 1)) { // TFSID 881510 touch events don't include `buttons`
      return;
    }

    const targetCell = this.getCellFromEventTarget(e.target);
    if (!targetCell) { return; }

    this.dispatchSelectionEvent(targetCell.colIdx, targetCell.rowIdx, e);
  }

  private rowsWithinViewPort(): number {
    const bodyRect = this.getBodyClientRect();
    return bodyRect.height / this.rowOuterHeight;
  }

  private handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = mergeEventHandlers(e => {
    this.setStateIfNew({ focusVisible: true });

    // This should be impossible but (at least in Chrome) we sometimes receive a `focusout` event with a missing `relatedTarget` field, even
    // though focus *is* moving to another element. When this happens, DataGrid gets into a bad state, so this is a safety check to get back
    // into a good state. TFSID 1220408
    if (this.state.focusLocation === 'outside') {
      // it's rude to assume implementation details, but it's also rude of the browser to not give us `relatedTarget`
      this.handleFocus(e as unknown as React.FocusEvent<HTMLElement>);
      return;
    }

    if (this.state.focusLocation === 'header') {
      // column headers are not currently focusable, but allow focus to escape if they contain something focusable
      if (e.key === 'ArrowDown') {
        this.focusInsideCell = false;
      } else {
        return;
      }
    } else if ((e.target as HTMLElement).getAttribute('role') !== 'gridcell' && this.focusInsideCell) {
      if (e.key === 'Escape') {
        this.focusInsideCell = false;
        this.focusActiveCell();
        e.stopPropagation();
        e.preventDefault();
      }

      return; // ignore all other key events that occur inside of cells, e.g. in an <input>
    }

    let colIdx = this.activeColIndex;
    let rowIdx = this.activeRowIndex;

    switch (e.key) {
      case 'Enter':
        this.focusInsideCell = true;
        this.focusActiveCell();
        e.stopPropagation();
        e.preventDefault();
        return;

      case 'a':
      case 'A':
      case ' ':
      case 'Escape':
        if (this.props.onSelectionEvent) {
          this.dispatchSelectionEvent(colIdx, rowIdx, e);
          e.stopPropagation();
          e.preventDefault();
        }
        return;

      case 'ArrowUp':
        rowIdx--;
        break;

      case 'ArrowDown':
        rowIdx++;
        break;

      case 'ArrowLeft':
        colIdx--;
        break;

      case 'ArrowRight':
        colIdx++;
        break;

      case 'PageUp':
        rowIdx -= Math.floor(this.rowsWithinViewPort());
        break;

      case 'PageDown':
        rowIdx += Math.floor(this.rowsWithinViewPort());
        break;

      case 'Home':
        if (e.ctrlKey) {
          rowIdx = 0;
        }
        colIdx = 0;
        break;

      case 'End':
        if (e.ctrlKey) {
          rowIdx = this.state.rows.length - 1;
        }
        colIdx = this.props.cols.length - 1;
        break;

      default:
        return;
    }
    colIdx = clamp(colIdx, 0, this.props.cols.length - 1);
    rowIdx = clamp(rowIdx, 0, this.state.rows.length - 1);

    if (colIdx !== this.activeColIndex || rowIdx !== this.activeRowIndex) {
      this.dispatchSelectionEvent(colIdx, rowIdx, e);
      this.focusCell(rowIdx, colIdx);

      e.stopPropagation();
    }

    e.preventDefault();
  }, e => this.props.onKeyDown?.(e));

  /**
   * This method uses the scroll position and column widths to determine the range of columns and rows that will
   * be rendered to the Virtual DOM. The range of columns and rows will overlap the viewport on all sides. The
   * first and last column/row indexes are then normalized against the first and last column/row indexes for the
   * corresponding bandings.
   */
  private getPageIndexesFromScrollPosition(scrollLeft: number, cols: DataGrid.Column[]): PageIndexes {
    return Paging.getSectionedPageIndexesForScrollPosition(
      scrollLeft,
      this.scrollTop,
      this.state.rows.length || Math.ceil(this.rowsWithinViewPort()),
      cols,
      this.state.columnWidths || {},
      this.getStateAverageColumnWidth(),
      this.getBodyClientRect(),
      this.rowOuterHeight,
      this.colsPerChunk,
      this.sectionRows
    );
  }

  /**
   * getProvisionalColumnWidths is used when we have no data rows, so can't measure the width of the data columns.
   * We just choose reasonable widths for the headers.
   */
  private getProvisionalColumnWidths(): DataGrid.ColumnWidths | null {
    // Only compute provisional column widths if the header widths have already
    // been measured
    if (!this.state.measuredColumnHeaderWidths) {
      return null;
    }

    // Find the total width of the measured headers
    let totalWidth = 0;
    let numberOfResizableColumns = 0;
    let cols = this.props.cols || [];
    cols.forEach((col: DataGrid.Column) => {
      if (col.preferredWidth) {
        totalWidth += col.preferredWidth;
      } else {
        if (this.state.measuredColumnHeaderWidths) {
          totalWidth += this.state.measuredColumnHeaderWidths[col.name];
        }
      }
      if (col.resizable !== false) {
        // default (undefined) is resizable
        numberOfResizableColumns++;
      }
    });

    if (this.props.showRowHeaders) {
      totalWidth += RowHeaderCell.Width;
    }

    // Allocate any extra space evenly between the resizable headers
    const extraAvailable = this.state.gridWidth - this.getScrollbarWidth() - totalWidth;
    let provisionalColumnWidths: DataGrid.ColumnWidths = {};
    if (extraAvailable > 0 && numberOfResizableColumns > 0) {
      let extraPerHeader = extraAvailable / numberOfResizableColumns;
      cols.forEach((col: DataGrid.Column) => {
        if (col.resizable !== false) {
          // default (undefined) is resizable
          let width: number = 0;
          if (col.preferredWidth) {
            width = col.preferredWidth;
          } else {
            if (this.state.measuredColumnHeaderWidths) {
              width = this.state.measuredColumnHeaderWidths[col.name];
            }
          }
          provisionalColumnWidths[col.name] = width + extraPerHeader;
        }
      });
    }
    return provisionalColumnWidths;
  }

  private onBodyScroll = (left: number, top: number) => {
    if (this.aboutToFocusCell) { return; }

    this.scrollTop = top;

    let partialStateUpdate = {} as DataGrid.State;
    if (left !== this.state.scrollLeft) {
      partialStateUpdate.scrollLeft = left;
    }

    const nextPageIndexes = this.getPageIndexesFromScrollPosition(left, this.props.cols);

    if (!this.arePageIndexesEqual(this.state.pageIndexes, nextPageIndexes)) {
      partialStateUpdate.pageIndexes = nextPageIndexes;
    }

    if (this.isFetchRequiredForPageIndexes(nextPageIndexes)) {
      this.fetchUsingPageIndexes(nextPageIndexes, partialStateUpdate);
    } else if (Object.keys(partialStateUpdate).length > 0) {
      this.setState(partialStateUpdate);
    }
  }

  private getColumnWidthInfo(): DataGrid.ColumnWidthInfo {
    let columnWidthInfo: DataGrid.ColumnWidthInfo = {
      leftSpacer: 0,
      totalColumnWidth: 0
    };

    const columnWidths = this.state.columnWidths || {};
    if (Object.keys(columnWidths).length === 0) {
      return columnWidthInfo;
    }

    const defaultColumnWidth = this.getStateAverageColumnWidth();
    const columnStartIndex = this.state.pageIndexes.startColIdx;

    let totalColumnWidth: number = this.props.showRowHeaders ? RowHeaderCell.Width : 0;
    for (let colIdx = 0; colIdx < this.props.cols.length; colIdx++) {
      totalColumnWidth += columnWidths[this.props.cols[colIdx].name] || defaultColumnWidth;
      if (columnStartIndex !== 0 && (colIdx + 1 === columnStartIndex)) {
        columnWidthInfo.leftSpacer = totalColumnWidth;
      }
    }
    columnWidthInfo.totalColumnWidth = totalColumnWidth;

    return columnWidthInfo;
  }

  private getStateAverageColumnWidth(): number {
    return Math.max(this.state.averageColumnWidth || 0, this.defaultAverageColumnWidth);
  }

  private calculateAverageColumnWidth(columnWidths: DataGrid.ColumnWidths): number {
    const colCount = Object.keys(columnWidths).length;
    if (colCount === 0) {
      return this.defaultAverageColumnWidth;
    }

    const sum = Object.keys(columnWidths).map(key => columnWidths[key]).reduce((acc, val) => {
      return acc + val;
    });

    const averageColumnWidth = Math.ceil(sum / colCount);
    return Math.max(averageColumnWidth, this.defaultAverageColumnWidth);
  }
}

export namespace DataGrid {

  export interface ColumnWidthInfo {
    leftSpacer: number;
    totalColumnWidth: number;
  }

  export interface Column {
    // A unique identifier of this column in the datagrid
    name: string;
    // Used to indicate which data field to display in this column
    dataKey: string;
    headerText: string;
    headerTooltip?: string;
    direction?: string;
    headerComponent?: React.ComponentType<HeaderColumn.HeaderFormatterProps>;
    /** @deprecated `React.createFactory` is deprecated; instead pass the component type directly in `headerComponent` prop */
    headerFormatter?: React.Factory<HeaderColumn.HeaderFormatterProps>;
    cellComponent?: React.ComponentType<BodyCell.Props>;
    /** @deprecated `React.createFactory` is deprecated; instead pass the component type directly in `cellComponent` prop */
    cellFormatter?: React.Factory<BodyCell.Props>;
    resizable?: boolean;
    sortable?: boolean;
    growToFit?: boolean;
    preferredWidth?: number;
    maxColumnWidth?: number;
  }

  export interface ColumnWidths {
    [key: string]: number;
  }

  export interface Row {
    key: string;
    /** key into this struct is the `Column.dataKey` */
    data: Dictionary<any>;
  }

  export interface RowStore {
    /**
     * Called by the grid to request row data.
     * @param start the first row that will be rendered per the current viewport.
     * @param end the last row that will be rendered per the current viewport.
     * @param rowHandler a callback which MUST be invoked asynchronously within the implementation of getItems.
     *        the caller shall invoke this callback to provide the row data to the grid. The `rows` array may be full or sparse.
     *        In either case, `rows.length` MUST represent the full size of the dataset, irrespective of any paging or array sparseness.
     *        Despite the API appearing to support it, asynchronous invocation causes a race condition
     *        and the data may not be rendered correctly.
     * If you want to implement an asynchronous data fetching in a custom row store, you can bypass the race condition
     * by wrapping the grid in a component that simulates a synchronous row store.
     *
     * See the below MR for an asynchronous example/details:
     * https://gitlab.tableausoftware.com/DataCatalog/data-catalog-client-components/merge_requests/249
     */
    getItems(start: number, end: number, rowHandler: (rows: Row[]) => void): void;
  }

  export interface ReportWidthFn {
    (columnKey: string, width: number): void;
  }

  export interface ScrollPositionFn {
    // tslint:disable-next-line:no-any
    (scrollLeft: number, scrollTop: number): any;
  }

  export interface ScrollComponentProps extends React.ClassAttributes<HTMLElement> {
    /**
     * A CSS class to add to the scroll root.
     */
    rootCssClass?: string;
    /**
     * An ID to add to the scroll root.
     * @deprecated contact Shared Widgets team before consuming
     */
    scrollingRootId?: string;
    /**
     * TODO: should handle margins on all sides.
     */
    marginTop?: number;
    /**
     * A callback for providing the current scroll.  Should be called whenever
     * the scroll position changes.
     */
    onScroll: ScrollPositionFn;
    /**
     * A callback for providing access to the IScrollable interface.  Called with
     * null when unmounted.
     */
    onRefUpdated: (scrollable: IScrollable | null) => void;
  }

  /**
   * An interface for interacting with the scroll handler instance.
   */
  // tslint:disable-next-line
  export interface IScrollable {

    getScrollPosition(): { scrollLeft: number, scrollTop: number };

    setScrollPosition(scrollLeft: number | null, scrollTop: number | null): void;

    scrollBy(x: number, y: number): void;

    getScrollbarWidth(): number;

    dispose(): void;

    /**
     * Resets scroll position back to its default/neutral state (usually x=0, y=0).
     */
    resetPosition(): void;

    /**
     * Function to be called by the consumer to inform the scrollable that the relevant DOM
     * has changed and therefore might need recomputation.
     */
    refresh(): void;

    handleEvent(e: TouchEvent): void;

    canScroll(): boolean;

    getScrollContainer(): Element | null;
  }

  /**
   * DataGrid level configuration options.  For any not specified default values will be used.
   */
  export interface Config {
    rowOuterHeight?: number;
    /**
     * The number of cols to render per grid section.
     */
    colsPerChunk?: number;
    /**
     * The number of rows to render per grid section.
     * Only the first section is looked at to calculate ideal cell widths.
     */
    sectionRows?: number;
    /**
     * Enforcing a minimum column width prevents some glitches when the columns
     * get very narrow. It would be ideal to find ways to remove the glitches and
     * reduce this minimum to 0. Defaults to 39.
     */
    minimumColumnWidth?: number;

    /**
     * The max column width allowed when initially auto-sizing columns.  Defaults
     * to 200.  A number < 0 indicates no maximum.
     */
    maxInitialColumnWidth?: number;
    /**
     * Specifying true will make the grid scroll to its last scroll position when re-rendering,
     * as oppose to the grid scroll back to the top left.
     */
    rememberLastScrollPos?: boolean;
  }

  export interface Props {
    cols: DataGrid.Column[];
    /**
     * This should be a row store that satisfies the RowStore interface as it is defined.
     * If implementing a custom row store (i.e. not using one supplied by this package),
     * be aware that async stores are not supported (see more details in the RowStore interface jsdoc).
     */
    rowStore?: DataGrid.RowStore;

    /**
     * @deprecated don't use this, it's leftover from the Angular transition before we started using React sanely.
     * Instead, do one of the following, if possible:
     *  - update a watched property (see angularDirectives.ts)
     *  - add a watch for the property you are really updating
     *  - add a Flux store
     */
    hackyUpdate?: number;
    sortKey?: string;
    sortAscending?: boolean;
    /**
     * @default true
     */
    showHeaders?: boolean;
    leftMargin?: number;

    /**
     * Show the alternate row banding colors. If undefined, defaults to true (i.e. shows the row banding).
     */
    showRowBanding?: boolean;

    /**
     * Show the row headers to the left of the first column of cells.
     */
    showRowHeaders?: boolean;

    /**
     * If true, show horizontal gridlines.
     */
    showHorizontalGridLines?: boolean;

    /**
     * Draw vertical grid lines to the right of each cell in the body
     */
    showVerticalGridLines?: boolean;

    /**
     * Callback on header cell click.
     */
    onHeaderClick?: ClickHandler;

    /**
     * Callback on header cell double click.
     */
    onHeaderDoubleClick?: ClickHandler;

    /**
     * Callback on header context menu (aka right click).
     */
    onHeaderContextMenu?: ClickHandler;

    /**
     * User-specified header height used to determine body's top positioning
     */
    headerHeight?: number;

    /**
     * Callback on cell content click.
     */
    onCellClick?: ClickHandler;

    /**
     * Callback on cell content double click.
     */
    onCellDoubleClick?: ClickHandler;

    /**
     * Callback on cell context menu (aka right click).
     */
    onCellContextMenu?: ClickHandler;

    /**
     * `RowKeyedGridSelection` is useful if your row indexes are unknowable or unstable. Keys are the `key` field of the
     * row, value is `true` if selected.
     *
     * `IndexBasedGridSelection` is more versatile in that it can have selection of individual cells/rows/cols, as well
     * as ranged selection. However the consumer must track what the indices mean in terms of values and
     * modify the IndexBasedGridSelection by providing `onSelectionEvent`, below.
     */
    selectionModel?: GridSelection;

    /**
     * Callback when the user wishes to change `selectionModel` via a keyboard or mouse event.
     * @returns true if the selection data is updated in response to event, false otherwise
     */
    onSelectionEvent?: (event: CellSelectionEvent) => boolean;

    /** If provided, this will be rendered as the body of the datagrid if there are no rows available (i.e. no row store, or 0 rows). */
    emptyChild?: React.ReactChild;

    updated?: (rows: DataGrid.Row[]) => void;
    /**
     * A custom scrolling component for the grid body.  If not specified will use native 'overflow: auto'.
     */
    scrollComponent?: React.ComponentClass<ScrollComponentProps>;

    /**
     * ID to put on the scroll root.
     * @deprecated contact Shared Widgets team before consuming
     */
    scrollingRootId?: string;

    /**
     * A callback for getting access to the IScrollable interface.
     */
    scrollableHandler?: (scrollable: IScrollable) => void;

    /**
     * Optional config options.  Default values will be used if not present.
     */
    config?: Config;

    // duplicated in the component definition but left here for backwards compatibility
    style?: React.CSSProperties;
  }

  export interface State {
    rows: DataGrid.Row[];
    measuredColumnHeaderWidths?: Dictionary<number>;
    measuredColumnBodyWidths?: Dictionary<number>;
    userSizedColumnWidths: Dictionary<number>;
    columnWidths?: DataGrid.ColumnWidths;
    averageColumnWidth?: number;
    scrollLeft: number;
    gridWidth: number;
    gridHeight: number;
    /** for perf: the cell ranges that specify which sections/pages of cells are actually rendered */
    pageIndexes: PageIndexes;

    focusLocation: FocusLocation;
    /** true iif focus indicator should be shown -- i.e. only for keyboard interactions (hide for mouse) */
    focusVisible: boolean;

    selectionTimestamp: number;
  }

  export interface CellSelectionEvent {
    cell: Cell;

    altKey: boolean;
    /** true if CTRL on Windows or CTRL or CMD (⌘) on Mac */
    ctrlKey: boolean;
    shiftKey: boolean;
    key?: string;

    button?: number;
    buttons?: number;

    type: string;
  }

  export interface Cell {
    /** `-1` means "all columns" */
    colIdx: number;
    /** `-1` means "all rows", `NaN` means "header" */
    rowIdx: number;
    colKey?: string;
    rowKey?: string;
  }

  export type ClickHandler = (rowData: any, cell: Cell, e: React.MouseEvent<HTMLDivElement>) => void;
}
